// UnifiedAddModal.js - Reusable modal for both collection and wantlist management
import React, { useState, useEffect } from 'react';
import BaseModal from './BaseModal';
import { useNotification } from '../contexts/NotificationContext';
import { updateChallengeProgress } from '../utils/challengeUtils';

const UnifiedAddModal = ({
  isOpen,
  onClose,
  movie,
  mode = 'collection', // 'collection' or 'wantlist'
  userCollectionItems,
  userWantlist,
  supabase,
  currentUser,
  onSuccess,
  addReleaseToWantlist,
  removeReleaseFromWantlist,
}) => {
  const { showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [releases, setReleases] = useState([]);
  const [selectedReleases, setSelectedReleases] = useState([]); // Multi-select support
  const [releaseNotes, setReleaseNotes] = useState({}); // Individual notes per release
  const [processingItems, setProcessingItems] = useState([]); // Track items being processed
  const [removedItems, setRemovedItems] = useState([]); // Track items marked for removal

  const isCollectionMode = mode === 'collection';
  const isWantlistMode = mode === 'wantlist';
  // Removed unused variables: userItems, addButtonText
  const modalTitle = isCollectionMode ? 'Manage Collection' : 'Manage Wantlist';
  const itemTypeName = isCollectionMode ? 'collection' : 'wantlist';

  // Load releases for movie
  useEffect(() => {
    if (!movie?.id || !isOpen) return;

    const loadReleases = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('movie_releases')
          .select('*')
          .eq('master_movie_id', movie.id)
          .order('release_year', { ascending: false });

        if (error) {
          console.error('Error loading releases:', error);
        } else {
          setReleases(data || []);
        }
      } catch (err) {
        console.error('Error loading releases:', err);
      } finally {
        setLoading(false);
      }
    };

    loadReleases();
  }, [movie?.id, supabase, isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReleases([]);
      setReleaseNotes({});
      setProcessingItems([]);
      setRemovedItems([]);
    }
  }, [isOpen]);

  // Check if a release is already in user's collection/wantlist
  const isReleaseInUserItems = releaseId => {
    if (isCollectionMode) {
      return userCollectionItems?.some(
        item => item.movie_releases?.id === releaseId || item.release_id === releaseId
      );
    } else {
      return userWantlist?.some(item => item.movie_releases?.id === releaseId);
    }
  };

  // Handle adding to collection
  const handleAddToCollection = async (releaseId, notes) => {
    try {
      const { error } = await supabase.from('user_collection_items').insert({
        user_id: currentUser.id,
        release_id: releaseId,
        personal_notes: notes || '',
      });

      if (error) {
        console.error('Error adding to collection:', error);
        if (error.code === '23505') {
          throw new Error('Already in collection');
        } else {
          throw error;
        }
      }

      // Update challenge progress for collection additions
      if (mode === 'collection') {
        await updateChallengeProgress(supabase, currentUser.id, {
          id: releaseId,
          title: movie?.title,
        });
      }
    } catch (err) {
      throw err;
    }
  };

  // Handle adding to wantlist
  const handleAddToWantlist = async (releaseDisplayId, notes) => {
    if (addReleaseToWantlist) {
      const result = await addReleaseToWantlist(releaseDisplayId, notes);
      if (!result.success) {
        throw new Error(result.error || 'Failed to add to wantlist');
      }
    } else {
      throw new Error('addReleaseToWantlist function not provided');
    }
  };

  // Handle removing from collection
  const handleRemoveFromCollection = async releaseId => {
    try {
      const collectionItem = userCollectionItems?.find(
        item => item.movie_releases?.id === releaseId || item.release_id === releaseId
      );

      if (collectionItem) {
        const { error } = await supabase
          .from('user_collection_items')
          .delete()
          .eq('id', collectionItem.id);

        if (error) {
          throw error;
        }
      }
    } catch (err) {
      throw err;
    }
  };

  // Handle removing from wantlist
  const handleRemoveFromWantlist = async releaseDisplayId => {
    if (removeReleaseFromWantlist) {
      const result = await removeReleaseFromWantlist(releaseDisplayId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove from wantlist');
      }
    } else {
      throw new Error('removeReleaseFromWantlist function not provided');
    }
  };

  // Handle save button click
  const handleSave = async () => {
    if (selectedReleases.length === 0 && removedItems.length === 0) {
      showError(`No changes to save`);
      return;
    }

    setProcessingItems([...selectedReleases.map(r => r.id), ...removedItems]);

    try {
      // Process removals first
      for (const itemId of removedItems) {
        if (isCollectionMode) {
          await handleRemoveFromCollection(itemId);
        } else {
          await handleRemoveFromWantlist(itemId);
        }
      }

      // Process additions
      for (const release of selectedReleases) {
        const notes = releaseNotes[release.id] || '';

        if (isCollectionMode) {
          await handleAddToCollection(release.id, notes);
        } else {
          await handleAddToWantlist(release.display_id, notes);
        }
      }

      // Success - reload data and close
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error(`Error saving changes to ${itemTypeName}:`, error);
      showError(`Error saving changes to ${itemTypeName}: ${error.message}`);
    } finally {
      setProcessingItems([]);
    }
  };

  // Handle individual item removal (for items already in collection/wantlist)
  // Now just tracks locally without immediate database deletion
  const handleRemoveItem = release => {
    if (isCollectionMode) {
      // Track for local removal from collection
      setRemovedItems(prev => [...prev, release.id]);
    } else {
      // Track for local removal from wantlist
      setRemovedItems(prev => [...prev, release.display_id]);
    }
  };

  const handleClose = () => {
    setSelectedReleases([]);
    setReleaseNotes({});
    setProcessingItems([]);
    setRemovedItems([]);
    onClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title={modalTitle} maxWidth="2xl">
      <div className="bg-gray-800 border border-gray-600 p-4 rounded-lg mb-4">
        <h3 className="font-medium text-white">
          {movie?.title} ({movie?.year})
        </h3>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="text-gray-300">Loading releases...</div>
        </div>
      ) : releases.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-300 mb-2">No releases found for this movie</div>
          <p className="text-sm text-gray-400">Releases must be added to the catalog first.</p>
        </div>
      ) : (
        <>
          {/* Release Selection Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              All Releases {releases.length > 1 ? `(${releases.length} available)` : ''}
            </label>

            <div className="space-y-2 max-h-64 overflow-y-auto dark-scrollbar">
              {releases.map(release => {
                const isInUserItems = isReleaseInUserItems(release.id);
                const isSelected = selectedReleases.some(r => r.id === release.id);
                const isProcessing = processingItems.includes(release.id);
                const isMarkedForRemoval = isCollectionMode
                  ? removedItems.includes(release.id)
                  : removedItems.includes(release.display_id);
                const userItem = isCollectionMode
                  ? userCollectionItems?.find(
                      item =>
                        item.movie_releases?.id === release.id || item.release_id === release.id
                    )
                  : userWantlist?.find(item => item.movie_releases?.id === release.id);

                return (
                  <div
                    key={release.id}
                    className={`border rounded-lg p-3 transition-colors ${
                      isProcessing
                        ? 'opacity-50 cursor-wait'
                        : isSelected
                          ? 'border-red-500 bg-red-900 cursor-pointer'
                          : isMarkedForRemoval
                            ? 'border-red-600 bg-red-900'
                            : isInUserItems
                              ? 'border-blue-600 bg-blue-900'
                              : 'border-gray-600 bg-gray-800 hover:border-red-500 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (isProcessing) return;

                      if (isInUserItems) {
                        // If item is already in user's collection/wantlist, toggle removal state
                        if (isMarkedForRemoval) {
                          // Remove from removal list (back to "keep" state)
                          if (isCollectionMode) {
                            setRemovedItems(prev => prev.filter(id => id !== release.id));
                          } else {
                            setRemovedItems(prev => prev.filter(id => id !== release.display_id));
                          }
                        } else {
                          // Add to removal list
                          handleRemoveItem(release);
                        }
                      } else {
                        // Handle selection for new items
                        setSelectedReleases(prev => {
                          const isCurrentlySelected = prev.some(r => r.id === release.id);
                          if (isCurrentlySelected) {
                            // Remove from selection and clear its notes
                            setReleaseNotes(prevNotes => {
                              const newNotes = { ...prevNotes };
                              delete newNotes[release.id];
                              return newNotes;
                            });
                            return prev.filter(r => r.id !== release.id);
                          } else {
                            // Add to selection
                            return [...prev, release];
                          }
                        });
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-medium text-red-400 text-sm">
                            {release.release_title || 'Standard Release'}
                          </h4>
                        </div>

                        <div className="text-xs text-gray-300 space-y-1">
                          {release.studio && (
                            <span className="mr-3">
                              <strong>Studio:</strong> {release.studio}
                            </span>
                          )}
                          {release.release_year && (
                            <span className="mr-3">
                              <strong>Year:</strong> {release.release_year}
                            </span>
                          )}
                          {release.case_type && (
                            <span>
                              <strong>Case:</strong> {release.case_type}
                            </span>
                          )}
                        </div>

                        {/* Show existing notes if item is already in collection/wantlist */}
                        {isInUserItems && userItem?.personal_notes && isCollectionMode && (
                          <div className="mt-2 text-xs text-gray-300 italic">
                            Notes: {userItem.personal_notes}
                          </div>
                        )}
                        {isInUserItems && userItem?.notes && isWantlistMode && (
                          <div className="mt-2 text-xs text-gray-300 italic">
                            Notes: {userItem.notes}
                          </div>
                        )}
                      </div>

                      <div className="ml-2 flex-shrink-0">
                        {isMarkedForRemoval ? (
                          <div className="w-5 h-5 border-2 border-red-400 rounded bg-red-900"></div>
                        ) : isInUserItems || isSelected ? (
                          <div className="w-5 h-5 bg-red-500 border-2 border-red-500 rounded flex items-center justify-center">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-5 h-5 border-2 border-gray-500 rounded"></div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Individual Notes Section for Selected Releases */}
          {(selectedReleases.length > 0 || removedItems.length > 0) && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {selectedReleases.length > 0
                  ? 'Notes for Selected Releases (optional)'
                  : 'Changes Pending'}
              </label>
              {selectedReleases.length === 0 && removedItems.length > 0 && (
                <p className="text-sm text-gray-300 mb-3">
                  Click Save Changes to apply your modifications.
                </p>
              )}
              <div className="space-y-3">
                {selectedReleases.map(release => (
                  <div
                    key={release.id}
                    className="border border-gray-600 bg-gray-800 rounded-lg p-3"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-sm text-red-400">
                        {release.release_title || 'Standard Release'}
                      </h4>
                      {release.studio && (
                        <span className="text-xs text-gray-400">• {release.studio}</span>
                      )}
                      {release.release_year && (
                        <span className="text-xs text-gray-400">• {release.release_year}</span>
                      )}
                    </div>
                    <textarea
                      placeholder={
                        isCollectionMode
                          ? 'Add personal notes for this release...'
                          : 'Add notes about why you want this release...'
                      }
                      value={releaseNotes[release.id] || ''}
                      onChange={e =>
                        setReleaseNotes(prev => ({
                          ...prev,
                          [release.id]: e.target.value,
                        }))
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none resize-none text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-600">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
        >
          Cancel
        </button>
        {releases.length > 0 && (
          <button
            onClick={handleSave}
            disabled={
              (selectedReleases.length === 0 && removedItems.length === 0) ||
              processingItems.length > 0
            }
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Save Changes</span>
            {(selectedReleases.length > 0 || removedItems.length > 0) && (
              <span className="bg-red-500 px-2 py-0.5 rounded text-xs">
                {selectedReleases.length + removedItems.length}
              </span>
            )}
          </button>
        )}
      </div>
    </BaseModal>
  );
};

export default UnifiedAddModal;
